import React, { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Grid, GizmoHelper, GizmoViewport, Environment } from '@react-three/drei';
import { useSceneStore } from '../../stores';
import { SceneObjects } from './SceneObjects';

interface Scene3DProps {
  className?: string;
}

export const Scene3D: React.FC<Scene3DProps> = ({ className = '' }) => {
  const { currentScene } = useSceneStore();

  if (!currentScene) {
    return (
      <div className={`flex items-center justify-center h-full bg-gray-100 ${className}`}>
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-600">No Scene Loaded</h3>
          <p className="text-gray-500">Create or load a scene to start editing</p>
        </div>
      </div>
    );
  }

  const { settings, camera } = currentScene;

  return (
    <div className={`relative h-full ${className}`}>
      <Canvas
        shadows={settings.shadows}
        camera={{
          position: camera.position,
          fov: camera.fov,
          near: camera.near,
          far: camera.far,
        }}
        gl={{
          preserveDrawingBuffer: true,
          antialias: true,
          alpha: false,
        }}
        style={{ background: settings.backgroundColor }}
      >
        {/* Lighting */}
        <ambientLight intensity={settings.ambientLight.intensity} />
        <directionalLight position={[10, 10, 5]} intensity={1} castShadow={settings.shadows} />

        {/* Environment */}
        <Suspense fallback={null}>
          <Environment preset="city" background={false} />
        </Suspense>

        {/* Grid */}
        {settings.showGrid && (
          <Grid
            args={[settings.gridSize, settings.gridDivisions]}
            position={[0, 0, 0]}
            cellSize={settings.gridSize / settings.gridDivisions}
            cellThickness={0.5}
            cellColor="#888888"
            sectionSize={settings.gridSize / 10}
            sectionThickness={1}
            sectionColor="#444444"
            fadeDistance={settings.gridSize * 2}
            fadeStrength={1}
            followCamera={false}
            infiniteGrid
          />
        )}

        {/* Gizmo helper */}
        <GizmoHelper alignment="bottom-right" margin={[80, 80]}>
          <GizmoViewport axisColors={['#ff0000', '#00ff00', '#0000ff']} labelColor="black" />
        </GizmoHelper>

        {/* Scene objects */}
        <Suspense fallback={null}>
          <SceneObjects />
        </Suspense>

        {/* Controls */}
        <OrbitControls
          makeDefault
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          minDistance={1}
          maxDistance={1000}
          minPolarAngle={0}
          maxPolarAngle={Math.PI}
        />
      </Canvas>
    </div>
  );
};
